import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";
import DepartmentTable from "./DepartmentTable";
import "react-toastify/dist/ReactToastify.css";
import ConfirmActionModal from "../../ConfirmDeleteModal";

const DepartmentList = () => {
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [doctors, setDoctors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

  const languageOptions = [
    { value: "en", label: "English" },
    { value: "bn", label: "Bangla" },
  ];

// Fetch departments and doctors
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [departmentResponse, doctorResponse] = await Promise.all([
          axios.get("https://api.muktihospital.com/api/department", {
            headers: { "x-api-key": API_KEY },
          }),
          axios.get("https://api.muktihospital.com/api/doctor", {
            headers: { "x-api-key": API_KEY },
          }),
        ]);

        let departmentData = Array.isArray(departmentResponse.data)
          ? departmentResponse.data
          : [];
        const doctorData = Array.isArray(doctorResponse.data)
          ? doctorResponse.data
          : [];

        // Sort departments by createdAt (descending) if available
        departmentData = departmentData.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dateB - dateA;
        });

        setDepartments(departmentData);
        setFilteredDepartments(departmentData);
        setDoctors(doctorData);
      } catch (error) {
        console.error("❌ Error fetching data:", error);
        toast.error("Failed to load data.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handle new department from AddDepartment
  useEffect(() => {
    if (location.state?.newDepartment) {
      setDepartments((prev) => [location.state.newDepartment, ...prev]);
      setFilteredDepartments((prev) => [location.state.newDepartment, ...prev]);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Filter departments based on search term and language
  const filterDepartments = (term, lang) => {
    const filtered = departments.filter((dep) => {
      const trans = dep.translations?.[lang] || {};
      return (
        trans.name?.toLowerCase().includes(term) ||
        trans.metaTitle?.toLowerCase().includes(term)
      );
    });
    setFilteredDepartments(filtered);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    filterDepartments(term, selectedLanguage);
  };

  const handleLanguageChange = (selectedOption) => {
    const lang = selectedOption.value;
    setSelectedLanguage(lang);
    filterDepartments(searchTerm, lang);
  };

  const openDeleteModal = (departmentIds) => {
    setDepartmentToDelete(departmentIds);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDepartmentToDelete(null);
  };

  const handleConfirmDelete = async (ids) => {
    const idArray = Array.isArray(ids) ? ids : [ids];
    if (idArray.length === 0) return;

    setIsLoading(true);
    try {
      await Promise.all(
        idArray.map((id) =>
          axios.delete(`https://api.muktihospital.com/api/department/${id}`, {
            headers: { "x-api-key": API_KEY },
          })
        )
      );
      const updatedDepartments = departments.filter(
        (department) => !idArray.includes(department.id)
      );
      setDepartments(updatedDepartments);
      setFilteredDepartments(updatedDepartments);
      toast.success(`Department${idArray.length > 1 ? "s" : ""} deleted successfully.`);
    } catch (error) {
      console.error("❌ Error deleting department(s):", error);
      toast.error("Failed to delete department(s).");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (id) => {
    navigate(`/edit-department/${id}`);
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Department"},
    { label: "All Department" },
  ];

  return (
    <div>
      <ToastContainer position="top-right" />
      <PageHeading title="Department List" breadcrumbs={breadcrumbs} />
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        ids={departmentToDelete}
        entityName="department"
        title="Confirm Delete"
        message={
          Array.isArray(departmentToDelete) && departmentToDelete.length > 1
            ? `Are you sure you want to delete these ${departmentToDelete.length} departments?`
            : "Are you sure you want to delete this department?"
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
      <div className="flex justify-between items-center mb-4">
        <DynamicSelect
          options={languageOptions}
          onChange={handleLanguageChange}
          value={languageOptions.find((option) => option.value === selectedLanguage)}
          isSearchable
        />
      </div>
      <DepartmentTable
        data={filteredDepartments}
        setData={setFilteredDepartments}
        searchTerm={searchTerm}
        handleSearch={handleSearch}
        openDeleteModal={openDeleteModal}
        handleEdit={handleEdit}
        selectedLanguage={selectedLanguage}
        doctors={doctors}
        isLoading={isLoading}
      />
    </div>
  );
};

export default DepartmentList;